<template>
  <div class="vfl-has-label">
    <label class="vfl-label" :class="classObject" :for="inputId">
      {{ floatLabel }}
    </label>
    <slot></slot>
  </div>
</template>

<script>
import { HasContainerMixin } from '@/modules/core/app/mixins/HasContainerMixin';
import { FloatLabelPresenter } from '@/modules/core/app/presenters/FloatLabelPresenter';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'float-label',
  mixins: [HasContainerMixin],
  props: {
    on: {
      type: Boolean,
      default: true
    },
    fixed: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    inputIsDisabled: {
      type: Boolean,
      default: false
    },
    dispatch: {
      type: Boolean,
      default: true
    },
    for: {
      type: String,
      default: null
    },
    multiSelectDefaultActive: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formEl: undefined,
      labelEl: undefined,
      isActive: false,
      isFocused: false,
      isActiveMultiSelect: this.multiSelectDefaultActive,
      presenter: this.$resolve(FloatLabelPresenter, this)
    };
  },
  computed: {
    inputId() {
      return this.for;
    },
    classObject() {
      return {
        'vfl-label-on-input':
          this.on &&
          (this.isActive || this.fixed || this.isActiveMultiSelect || this.inputIsDisabled),
        'vfl-label-on-focus': this.isFocused
      };
    },
    formElType() {
      return this.formEl ? this.formEl.tagName.toLowerCase() : '';
    },
    floatLabel() {
      if (this.label) return this.label;

      switch (this.formElType) {
        case 'input':
        case 'textarea':
          return this.formEl.placeholder;
        case 'select':
          return this.formEl.querySelector('option[disabled][selected]').innerHTML;
        default:
          return '';
      }
    }
  },
  methods: {
    dispatchInput() {
      if (!this.dispatch || !this.formEl.value) {
        return;
      }

      this.presenter.dispatchInputEvent(this.formEl);
    },
    focusFormEl() {
      this.formEl.focus();
    },
    updateIsActive(e) {
      this.isActive = e.target.value.length > 0;
    },
    updateIsFocused(e) {
      this.isFocused = e.target === document.activeElement && this.isActive;
      if (e?.target?.className === 'multiselect__input') {
        if (e?.type === 'blur') {
          this.isActiveMultiSelect = true;
        } else if (e?.type === 'focus') {
          this.isActiveMultiSelect = false;
        }
      }
    }
  },
  mounted() {
    this.formEl = this.presenter.getFormElement();
    this.formEl.addEventListener('input', this.updateIsActive);
    this.formEl.addEventListener('input', this.updateIsFocused);
    this.formEl.addEventListener('blur', this.updateIsFocused);
    this.formEl.addEventListener('focus', this.updateIsFocused);

    if (!this.for) {
      this.labelEl = this.presenter.getLabelElement();
      this.labelEl.addEventListener('click', this.focusFormEl);
    }

    this.dispatchInput();
  },
  beforeUnmount() {
    this.formEl.removeEventListener('input', this.updateIsActive);
    this.formEl.removeEventListener('input', this.updateIsFocused);
    this.formEl.removeEventListener('blur', this.updateIsFocused);
    this.formEl.removeEventListener('focus', this.updateIsFocused);
  }
};
</script>

<style scoped>
.vfl-has-label {
  position: relative;
}

.vfl-label {
  position: absolute;
  top: var(--ce-10px);
  right: auto;
  left: var(--ce-8px);
  overflow: hidden;
  font-size: var(--ce-14px);
  color: var(--ce-grey-light-10);
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: top 0.2s ease-out;
  z-index: var(--ce-z-index-floating-label);
  background: var(--ce-white);
  display: inline-block;
  padding: 0 var(--ce-8px);
}

.vfl-label-on-input {
  top: var(--ce--8px);
  pointer-events: all;
  opacity: 1;
  font-size: var(--ce-12px);
}

.vfl-label-on-input :deep(.multiselect__tags) {
  margin-top: var(--ce-4px);
}

.vfl-label-on-focus {
  color: var(--ce-primary);
}

.vfl-has-label {
  &.input:focus,
  &.textarea:focus,
  &.select:focus {
    outline: 0;
  }
}
</style>
